import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  language: string;
  isSuperadmin?: boolean;
  role?: string;
}

interface Workspace {
  id: string;
  name: string;
  role: string;
  logoUrl?: string | null;
  brandColor?: string;
  accentColor?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  currentWorkspace: Workspace | null;
  workspaces: Workspace[];
  isAuthenticated: boolean;
}

// Load persisted state from localStorage
const loadPersistedState = (): Partial<AuthState> => {
  try {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    const workspacesStr = localStorage.getItem('workspaces');
    const currentWorkspaceStr = localStorage.getItem('currentWorkspace');

    return {
      token: token || null,
      user: userStr ? JSON.parse(userStr) : null,
      workspaces: workspacesStr ? JSON.parse(workspacesStr) : [],
      currentWorkspace: currentWorkspaceStr ? JSON.parse(currentWorkspaceStr) : null,
      isAuthenticated: !!token,
    };
  } catch (error) {
    console.error('Error loading persisted state:', error);
    return {
      token: null,
      user: null,
      workspaces: [],
      currentWorkspace: null,
      isAuthenticated: false,
    };
  }
};

const persistedState = loadPersistedState();

const initialState: AuthState = {
  user: persistedState.user || null,
  token: persistedState.token || null,
  currentWorkspace: persistedState.currentWorkspace || null,
  workspaces: persistedState.workspaces || [],
  isAuthenticated: persistedState.isAuthenticated || false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{
        user: User;
        token: string;
        workspaces: Workspace[];
        currentWorkspace?: Workspace;
      }>
    ) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.workspaces = action.payload.workspaces;
      state.currentWorkspace = action.payload.currentWorkspace || null;
      state.isAuthenticated = true;
      
      // Persist to localStorage
      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('user', JSON.stringify(action.payload.user));
      localStorage.setItem('workspaces', JSON.stringify(action.payload.workspaces));
      if (action.payload.currentWorkspace) {
        localStorage.setItem('currentWorkspace', JSON.stringify(action.payload.currentWorkspace));
      }
    },
    setCurrentWorkspace: (state, action: PayloadAction<Workspace>) => {
      state.currentWorkspace = action.payload;
      localStorage.setItem('currentWorkspace', JSON.stringify(action.payload));
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.currentWorkspace = null;
      state.workspaces = [];
      state.isAuthenticated = false;
      
      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('workspaces');
      localStorage.removeItem('currentWorkspace');
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        localStorage.setItem('user', JSON.stringify(state.user));
      }
    },
  },
});

export const { setCredentials, setCurrentWorkspace, logout, updateUser } =
  authSlice.actions;

export default authSlice.reducer;
